import React, {Component} from 'react';

import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import requiredIf from 'react-required-if';

import {InfoIcon, Input} from '@edume/magnificent';

import {Hint, ListInput, TextEditor} from '../';

import styles from './styles.module.scss';

const cx = classnames.bind(styles);

const DEFAULT_TITLE_LIMIT = 45;

class InputWithWarning extends Component {
  state = {
    isHintVisible: false,
    hasDismissedHint: false,
    hasFocus: false,
  };

  getLimit = () => {
    if (this.props.limit) {
      return this.props.limit;
    } else if (this.props.isTitle) {
      return DEFAULT_TITLE_LIMIT;
    }
    return null;
  };

  checkLimit = (value) => {
    const {isHintVisible, hasDismissedHint} = this.state;
    const limit = this.getLimit();

    if (!hasDismissedHint) {
      if (!isHintVisible && value.length > limit) {
        this.setState({isHintVisible: true});
      } else if (isHintVisible && value.length <= limit) {
        this.setState({isHintVisible: false});
      }
    }
  };

  onChangeValue = (value) => {
    this.checkLimit(value);
    this.props.onChangeValue(value);
  };

  dismissHint = () => {
    this.setState({hasDismissedHint: true, isHintVisible: false});
  };

  handleFocus = (e) => {
    this.checkLimit(e.value);
    this.setState({hasFocus: true});
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleMarkdownFocus = () => {
    this.checkLimit(this.props.value);
    this.setState({hasFocus: true});
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  };

  handleBlur = () => {
    this.setState({hasFocus: false});
    if (this.props.onBlur) {
      this.props.onBlur();
    }
  };

  /**
   * @param {string} oldValidationStyle
   * @returns string New Validation Style for Input Component
   */
  mapValidationStyle = (oldValidationStyle) => {
    if (oldValidationStyle === 'in-label' || oldValidationStyle === 'default') {
      return oldValidationStyle;
    }
    if (oldValidationStyle === 'icon') {
      return 'in-label';
    }
    if (oldValidationStyle === 'normal') {
      return 'default';
    }
    return undefined;
  };

  render() {
    const {
      type,
      rows,
      value,
      placeholder,
      title,
      isTitle,
      errorText,
      optionalText,
      isInvalid,
      autoFocus,
      disabled,
      validationStyles,
      ignorePrefilled,
      includeBottomMargin,
      errorKey,
      deleteEnabled,
      onDelete,
      deleteTooltipKey,
      disabledTooltipKey,
      onEnterKey,
      theme,
      hintPosition,
      hintVerticalPosition,
      intl,
      dataAutomation,
      readyStatusDataAutomation,
      showMarkdownToolbar,
      showMarkdownLists,
    } = this.props;
    const limit = this.getLimit();

    const characterCount = value.length;
    const countText =
      characterCount === 0
        ? ''
        : intl.formatMessage(
            {id: 'Input.characterCount'},
            {count: characterCount}
          );

    const longTextHintId = isTitle && !limit ? 'LONG_TITLE_TEXT' : 'LONG_TEXT';

    const getInputComponent = () => {
      switch (type) {
        case 'richText':
          return (
            <TextEditor
              isInvalid={isInvalid}
              title={title}
              validationStyles='icon'
              optionalText={optionalText}
              value={value}
              placeholder={placeholder}
              onChangeValue={this.onChangeValue}
              onFocus={this.handleMarkdownFocus}
              onBlur={this.handleBlur}
              dataAutomation={dataAutomation}
              readyStatusDataAutomation={readyStatusDataAutomation}
              showMarkdownToolbar={showMarkdownToolbar}
              showMarkdownLists={showMarkdownLists}
            />
          );
        case 'list':
          return (
            <ListInput
              type='text'
              value={value}
              onChangeValue={this.onChangeValue}
              placeholder={placeholder}
              isInvalid={isInvalid}
              errorKey={errorKey}
              title={title}
              validationStyles={validationStyles}
              includeBottomMargin={includeBottomMargin}
              deleteEnabled={deleteEnabled}
              onDelete={onDelete}
              deleteTooltipKey={deleteTooltipKey}
              disabledTooltipKey={disabledTooltipKey}
              countText={countText}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              theme={theme}
              dataAutomation={dataAutomation}
              readyStatusDataAutomation={readyStatusDataAutomation}
            />
          );

        default:
          return (
            <Input
              value={value}
              onChangeValue={this.onChangeValue}
              rows={rows}
              placeholder={placeholder}
              label={title}
              errorText={errorText}
              optionalText={optionalText}
              onEnterKey={onEnterKey}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              validationStyle={this.mapValidationStyle(validationStyles)}
              ignorePrefilled={ignorePrefilled}
              isInvalid={isInvalid}
              autoFocus={autoFocus}
              isDisabled={disabled}
              reserveValidationSpace
              includeBottomMargin={includeBottomMargin}
              theme={theme}
              showCount={true}
              countText={type === 'richText' ? '' : countText}
              useLegacyIcons={true}
              dataAutomation={dataAutomation}
              readyStatusDataAutomation={readyStatusDataAutomation}
            />
          );
      }
    };

    return (
      <div className={cx('hint-container')}>
        <div
          className={cx('floating-hint', hintPosition, hintVerticalPosition)}
        >
          <Hint
            hintId={longTextHintId}
            textValues={{
              count: characterCount,
              charsOverLimit: characterCount - limit,
            }}
            buttonKey='ok'
            trianglePosition={hintVerticalPosition === 'below' ? 'tl' : 'bl'}
            icon={<InfoIcon />}
            dismissHint={this.dismissHint}
            visible={this.state.isHintVisible && this.state.hasFocus}
          />
        </div>
        <div>{getInputComponent()}</div>
      </div>
    );
  }
}

InputWithWarning.propTypes = {
  // Input props
  value: PropTypes.string,
  onChangeValue: PropTypes.func.isRequired,
  rows: PropTypes.number,
  placeholder: PropTypes.string.isRequired,
  title: PropTypes.string,
  isTitle: PropTypes.bool,
  errorText: PropTypes.string,
  optionalText: PropTypes.string,
  onEnterKey: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  validationStyles: PropTypes.oneOf(['normal', 'alwaysValid', 'hide', 'icon']),
  isInvalid: PropTypes.bool.isRequired,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  ignorePrefilled: PropTypes.bool,
  includeBottomMargin: PropTypes.bool,
  theme: PropTypes.oneOf(['light', 'dark']),
  readyStatusDataAutomation: PropTypes.string,
  showMarkdownToolbar: PropTypes.bool,
  showMarkdownLists: PropTypes.bool,

  // ListInput props
  errorKey: PropTypes.string,
  deleteEnabled: PropTypes.bool,
  onDelete: PropTypes.func,
  deleteTooltipKey: PropTypes.string,
  disabledTooltipKey: PropTypes.string,

  // Other
  limit: requiredIf(PropTypes.number, (props) => !props.isTitle),
  type: PropTypes.oneOf(['normal', 'list', 'richText']),
  hintPosition: PropTypes.oneOf(['right', 'center']),
  hintVerticalPosition: PropTypes.oneOf(['above', 'below']),
  dataAutomation: PropTypes.string,

  intl: PropTypes.object.isRequired,
};

InputWithWarning.defaultProps = {
  hintPosition: 'right',
  hintVerticalPosition: 'above',
};

export default injectIntl(InputWithWarning);
